/**
 * People · Alderperson's office, top of page (screenshots/22). From the starter, wired to the API's OfficeTop.
 * Rule from design review: siblings are always the same size.
 *   - Signal strip: 4 equal tiles (104px). Each tile is a filter for the table below.
 *   - 3 equal cards (330px): header, exactly 4 rows of 56px, footer link pinned to the bottom.
 *     Text is one line and truncates; cards never grow.
 *   - Table toolbar: type tabs with counts + search + sort.
 */
import type { ReactNode } from "react";
import { ArrowDownUp, ArrowRight, Bell, Briefcase, Building2, Handshake, Mic, Scale, ScrollText } from "lucide-react";
import { Face } from "../components/shared/Face";
import { List, ListRow, RowCell, RowLead, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { CountBadge, Switch, TabBar } from "../components/shared/ui";
import { PersonImg, StanceChip } from "./atoms";
import { SearchBox } from "./Directory";
import type { CardRow, DirectoryQuery, OfficeTop } from "./model";

export type OfficeFilter = NonNullable<DirectoryQuery["office"]>;

export function OfficeOverview({ data, filter, onFilter, onToggleAlerts, canToggleAlerts }: {
  data: OfficeTop; filter: OfficeFilter; onFilter: (f: OfficeFilter) => void; onToggleAlerts: (on: boolean) => void; canToggleAlerts: boolean;
}) {
  const l = data.constituents.leaning;
  return (
    <div className="office-overview">
      <div className="signal-strip" role="group" aria-label="Filters">
        <Tile icon={<Mic size={14} />} label={`Ward ${data.ward} constituents who spoke`} value={data.constituents.total} active={filter === "constituents"} onClick={() => onFilter("constituents")}>
          {l && <>
            <span className="lean-bar" role="img" aria-label={`AI estimates: ${l.prog}% progressive, ${l.mod}% moderate, ${l.cons}% conservative`}>
              <span className="is-prog" style={{ flexGrow: l.prog }} /><span className="is-mod" style={{ flexGrow: l.mod }} /><span className="is-cons" style={{ flexGrow: l.cons }} />
            </span>
            <small>{l.prog}% prog · {l.mod}% mod · {l.cons}% cons</small>
          </>}
        </Tile>
        <Tile icon={<Handshake size={14} />} label="Allies · 75%+ aligned" value={data.alignment.allies} active={filter === "allies"} onClick={() => onFilter("allies")}>
          <small>of {data.alignment.colleagues} colleagues</small>
        </Tile>
        <Tile icon={<Scale size={14} />} label="Persuadable · 40–60%" value={data.alignment.persuadable} active={filter === "persuadable"} onClick={() => onFilter("persuadable")}>
          <small>{data.alignment.persuadableOnItems} on your items</small>
        </Tile>
        <Tile icon={<Briefcase size={14} />} label="Lobbyists on your items" value={data.lobbying.lobbyists} active={filter === "lobbyists"} onClick={() => onFilter("lobbyists")}>
          {data.lobbying.newCount > 0 && <span className="new-badge">{data.lobbying.newCount} new</span>}
        </Tile>
      </div>

      <div className="equal-cards-3">
        <EqualCard title="Constituents who testified" count={data.constituents.total} sub={`What Ward ${data.ward} residents asked for this term`}
          rows={data.constituents.rows} footer={<button className="link-arrow" onClick={() => onFilter("constituents")}>See all {data.constituents.total} constituents<ArrowRight size={14} /></button>} />
        <EqualCard title="Allies & opponents" count={data.alignment.colleagues} sub={`Alignment with ${data.member} this term`}
          rows={data.alignment.rows} footer={<button className="link-arrow" onClick={() => onFilter("colleagues")}>See all {data.alignment.colleagues} colleagues<ArrowRight size={14} /></button>} />
        <EqualCard title="Lobbying on your items" count={data.lobbying.lobbyists} sub={`On items ${data.member} sponsors`}
          rows={[...data.lobbying.rows.slice(0, 3), { id: "alerts", title: "Testimony alerts", sub: "Team alert when anyone speaks", lead: { kind: "icon", icon: "bell" } }]}
          trailFor={(id) => (id === "alerts" ? (canToggleAlerts ? <Switch on={data.testimonyAlerts} onChange={onToggleAlerts} label="Testimony alerts" /> : <span className="switch" role="switch" aria-checked={data.testimonyAlerts} aria-disabled aria-label="Testimony alerts"><span /></span>) : undefined)}
          footer={<button className="link-arrow" onClick={() => onFilter("lobbyists")}>See all lobbying<ArrowRight size={14} /></button>} />
      </div>
    </div>
  );
}

export function OfficeToolbar({ data, filter, onFilter, q, onQ, sort, onSort }: {
  data: OfficeTop; filter: OfficeFilter; onFilter: (f: OfficeFilter) => void; q: string; onQ: (v: string) => void; sort: DirectoryQuery["sort"]; onSort: () => void;
}) {
  return (
    <div className="table-toolbar">
      <TabBar label="People type" active={filter === "allies" || filter === "persuadable" ? "everyone" : filter} onChange={onFilter} tabs={[
        { id: "everyone", label: "Everyone" },
        { id: "colleagues", label: "Colleagues", count: data.counts.colleagues },
        { id: "constituents", label: "Your constituents", count: data.counts.constituents },
        { id: "lobbyists", label: "Lobbyists", count: data.counts.lobbyists },
        { id: "tagged", label: "Tagged by your team", count: data.counts.tagged },
      ] as const} />
      <div className="row-gap">
        <SearchBox placeholder="Search people" value={q} onChange={onQ} width={240} />
        <button className="btn" onClick={onSort}><ArrowDownUp size={14} aria-hidden />{sort === "recent" ? "Last spoke" : sort === "active" ? "Most active" : "Name"}</button>
      </div>
    </div>
  );
}

function Tile({ icon, label, value, active, onClick, children }: {
  icon: ReactNode; label: string; value: number; active: boolean; onClick: () => void; children?: ReactNode;
}) {
  return (
    <button className="signal-tile" aria-pressed={active} onClick={onClick}>
      <span className="signal-tile__label">{icon}{label}</span>
      <span className="signal-tile__value"><b className="live-number">{value}</b><span className="signal-tile__extra">{children}</span></span>
    </button>
  );
}

const ROWS = 4;

function EqualCard({ title, count, sub, rows, trailFor, footer }: {
  title: string; count: number; sub: string; rows: CardRow[]; trailFor?: (id: string) => ReactNode; footer: ReactNode;
}) {
  const shown = rows.slice(0, ROWS);
  return (
    <section className="equal-card">
      <header><h3>{title}</h3><CountBadge>{count}</CountBadge><p>{sub}</p></header>
      <List className="equal-card__list">
        {shown.map((r) => (
          <ListRow key={r.id} className="equal-card__row" wrap={false}>
            <RowLead><Lead lead={r.lead} name={r.title} /></RowLead>
            <RowMain min={0} className="equal-card__text">
              <RowTitle>{r.href ? <a href={r.href}><b>{r.title}</b></a> : <b>{r.title}</b>}</RowTitle>
              <RowSub><small>{r.sub}</small></RowSub>
            </RowMain>
            <RowCell width={84} align="end" className="equal-card__trail">{trailFor?.(r.id) ?? <Trail trail={r.trail} />}</RowCell>
          </ListRow>
        ))}
        {/* keep the height fixed when there are fewer than 4 rows */}
        {Array.from({ length: ROWS - shown.length }, (_, i) => <li key={`pad-${i}`} className="ls-row equal-card__row" aria-hidden />)}
      </List>
      <footer>{footer}</footer>
    </section>
  );
}

const ICONS = { building: Building2, scroll: ScrollText, bell: Bell } as const;

function Lead({ lead, name }: { lead: CardRow["lead"]; name: string }) {
  if (lead.kind === "face") return <Face name={lead.name} photoUrl={lead.photoUrl} size={36} />;
  if (lead.kind === "frame") return lead.url ? <PersonImg image={{ kind: "frame", url: lead.url, meetingId: "", at: 0 }} name={name} w={36} h={36} /> : <PersonImg image={{ kind: "none" }} name={name} w={36} h={36} />;
  const Icon = ICONS[lead.icon];
  return <span className={`icon-tile${lead.icon === "scroll" ? " icon-tile--legislation" : ""}`}><Icon size={16} aria-hidden /></span>;
}

function Trail({ trail }: { trail?: CardRow["trail"] }) {
  if (!trail) return null;
  if (trail.kind === "chip") return <span className="chip chip--neutral p-chip-sm">{trail.text}</span>;
  if (trail.kind === "align") return <Alignment pct={trail.pct} />;
  return <StanceChip stance={trail.stance} />;
}

const Alignment = ({ pct }: { pct: number }) => (
  <span className="alignment" data-tone={pct >= 50 ? "ally" : "opponent"}>
    <b>{pct}%</b><span className="alignment__track"><span style={{ width: `${pct}%` }} /></span>
  </span>
);
